import { useQuery } from "@tanstack/vue-query";
import { type MaybeRef } from "@vueuse/core";
import { api } from "@//api";
import { BOOKINGS_QUERY_KEY, type BookingStatus } from "./bookings";
import { type ListBookingsQuery } from "./listBookings";

// The bookings calendar (tape chart) only needs a handful of flat fields per
// booking — it never reads live pitch availability or current_booking. This
// hits the slim `/bookings/calendar` endpoint, which serialises exactly these
// fields and so avoids the per-pitch resolver N+1 the full `/bookings` schema
// incurs (thousands of extra queries on the all-sites month view).
export type CalendarBooking = {
  id: string;
  bookingRef: string;
  startDate: string;
  endDate: string;
  status: BookingStatus;
  balanceDue: number;
  bookingType:
    | "default"
    | "storage"
    | "seasonal"
    | "ferry"
    | "pitchup"
    | "experience_freedom";
  siteId: string;
  pitchNumber: string | null;
  customer: { firstName: string | null; lastName: string | null };
  // Pitch is named, not numbered (migration 0086). Experience Freedom units are
  // Pitches too (migration 0084), so EF bookings arrive on `pitch` like any other.
  pitch: { id: string; name: string } | null;
  reserved: { id: string; name: string } | null;
};

export const listCalendarBookings = async (query: ListBookingsQuery) => {
  const { body } = await api.get<Paginate<CalendarBooking[]>>(
    "/bookings/calendar",
    { query },
  );
  return body;
};

export const useCalendarBookings = (query: MaybeRef<ListBookingsQuery>) => {
  const { id: currentSiteId } = useCurrentSite();

  return useQuery({
    // Kept under the shared "bookings" prefix so booking mutations
    // (invalidateQueries([BOOKINGS_QUERY_KEY])) refresh the calendar and the
    // sessionStorage persister (which persists the "bookings" family) restores it.
    queryKey: [
      BOOKINGS_QUERY_KEY,
      "calendar",
      computed(() => ({
        ...unref(query),
        siteId: currentSiteId.value,
      })),
    ] as const,
    queryFn: ({ queryKey: [, , filter_] }) =>
      listCalendarBookings(unref(filter_)),
    // The tape chart is expensive to re-render; changing month/filters or
    // reopening the view refetches, so no background poll here.
    refetchInterval: false,
  });
};
